import { useMemo } from 'react';
import useQuery from '../../hooks/useQuery';
import permissions from '../../Apollo/permissions';
import isAllowedToReadAuditLogDebugData from '../isAllowedToReadAuditLogDebugData';
import { composedMap } from '../shape-map';
import { resolveRow } from '../shape-map/resolver';
import { normalizeAuditRow } from './normalize-audit-row';
import { resolveEligibleRows, selectAliasTargets, buildPredecessorMap } from './predecessor-elision';
import { buildDocument, buildVariables } from './predecessor-query-builder';
import { deriveQueryStatus } from './query-status';
import { DEFAULT_SORT_DIRECTION } from './paging/sort-direction';

export default function useAuditLogPredecessors({ rows, hasActiveFilters, sortDirection = DEFAULT_SORT_DIRECTION }) {
  // Elision reads a group's predecessor as the NEXT entry in the array, which is only the
  // chronologically preceding row while rows are newest-first. Sorting the table oldest-first would
  // otherwise pair each row with its successor and render a Previous value the record never held.
  const historyOrderedRows = useMemo(
    () => (sortDirection === 'asc' ? [...rows].reverse() : rows),
    [rows, sortDirection]
  );
  const eligibleRows = useMemo(() => resolveEligibleRows(composedMap, historyOrderedRows), [historyOrderedRows]);
  const aliasTargets = useMemo(
    () => selectAliasTargets(eligibleRows, { hasActiveFilters }),
    [eligibleRows, hasActiveFilters]
  );
  const targetRows = useMemo(
    () => aliasTargets.map(({ row, resolved }) => ({ ...row, identity: resolved.identity })),
    [aliasTargets]
  );

  const document = useMemo(() => buildDocument(targetRows), [targetRows]);
  const variables = useMemo(() => buildVariables(targetRows), [targetRows]);

  // A predecessor row carries debugData like any other; skipping keeps it out of the response for a
  // role that may not read it, and the diffs it feeds are never rendered for that role anyway.
  const { data, loading, error } = useQuery(document, {
    variables,
    skip: targetRows.length === 0 || !isAllowedToReadAuditLogDebugData(),
    fetchPolicy: 'network-only',
    context: { permission: permissions.readAuditLogDebugData },
  });

  const fetchedByRowId = useMemo(() => {
    const fetched = new Map();
    aliasTargets.forEach(({ row }, index) => {
      const raw = data?.[`p${index}`]?.[0] ?? null;
      fetched.set(row.id, raw ? normalizeAuditRow(raw) : null);
    });
    return fetched;
  }, [data, aliasTargets]);

  const predecessorByRowId = useMemo(
    () => buildPredecessorMap(eligibleRows, { hasActiveFilters, fetchedByRowId }),
    [eligibleRows, hasActiveFilters, fetchedByRowId]
  );

  const predecessorEntryByRowId = useMemo(() => {
    const entries = new Map();
    predecessorByRowId.forEach((predecessorRow, rowId) => {
      entries.set(rowId, predecessorRow ? resolveRow(composedMap, predecessorRow).entry : null);
    });
    return entries;
  }, [predecessorByRowId]);

  const status = deriveQueryStatus({ loading, error, isEmpty: false });

  return { predecessorByRowId, predecessorEntryByRowId, loading, error, status };
}
